import React, { useEffect, useMemo, useState } from 'react';

interface AnimatedBackgroundProps {
  variant?: 'default' | 'subtle';
  className?: string;
}

interface ShootingStar {
  id: string;
  startX: number;
  startY: number;
  angle: number;
  speed: number;
  size: number;
  opacity: number;
  length: number;
}

interface Particle {
  left: string;
  top: string;
  size: number;
  opacity: number;
  hasHalo: boolean;
  haloSize: number;
  haloOpacity: number;
}

// Generate stable particles outside component to prevent re-renders
const particles: Particle[] = [...Array(200)].map(() => {
  const sizeRandom = Math.pow(Math.random(), 2);
  const size = 0.5 + sizeRandom * 2;
  const opacity = 0.2 + (size / 2.5) * 0.6;
  const hasHalo = size > 1.5;
  const haloSize = hasHalo ? size * 4 : 0;
  const haloOpacity = hasHalo ? opacity * 0.2 : 0;

  return {
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    size,
    opacity,
    hasHalo,
    haloSize,
    haloOpacity,
  };
});

export function AnimatedBackground({
  variant = 'default',
  className = '',
}: AnimatedBackgroundProps) {
  const [shootingStars, setShootingStars] = useState<ShootingStar[]>([]);

  const createShootingStar = useMemo(
    () => () => {
      // Randomly choose a starting edge (top, right, bottom, left)
      const edge = Math.floor(Math.random() * 4);
      let startX: number, startY: number;
      let angle: number;

      switch (edge) {
        case 0: // Top
          startX = Math.random() * 120 - 10; // Start outside screen
          startY = -10; // Start above screen
          angle = Math.PI / 4 + (Math.random() * Math.PI) / 2; // 45-135 degrees
          break;
        case 1: // Right
          startX = 110; // Start outside screen
          startY = Math.random() * 120 - 10;
          angle = Math.PI / 2 + (Math.random() * Math.PI) / 2; // 135-225 degrees
          break;
        case 2: // Bottom
          startX = Math.random() * 120 - 10;
          startY = 110; // Start below screen
          angle = Math.PI + (Math.random() * Math.PI) / 2; // 225-315 degrees
          break;
        default: // Left
          startX = -10; // Start outside screen
          startY = Math.random() * 120 - 10;
          angle = -Math.PI / 4 + (Math.random() * Math.PI) / 2; // -45 to 45 degrees
          break;
      }

      // Randomize between thin and slightly thicker stars
      const isThicker = Math.random() > 0.7;
      const baseSize = isThicker ? 1.2 : 0.8;
      const baseOpacity = isThicker ? 0.8 : 0.6;
      const baseLength = isThicker ? 80 : 60;

      return {
        id: Math.random().toString(),
        startX,
        startY,
        angle,
        speed: 0.5 + Math.random() * 0.1, // Much faster speed
        size: baseSize + Math.random() * 0.4,
        opacity: baseOpacity + Math.random() * 0.2,
        length: baseLength + Math.random() * 20,
      };
    },
    []
  );

  useEffect(() => {
    // Add new shooting star every 4-8 seconds
    const interval = setInterval(
      () => {
        setShootingStars(prev => [...prev, createShootingStar()]);
      },
      4000 + Math.random() * 4000
    );

    // Remove shooting stars after animation
    const cleanup = setInterval(() => {
      setShootingStars(prev =>
        prev.filter(star => {
          const element = document.getElementById(star.id);
          return element && !element.classList.contains('completed');
        })
      );
    }, 500);

    return () => {
      clearInterval(interval);
      clearInterval(cleanup);
    };
  }, [createShootingStar]);

  return (
    <div className={`absolute inset-0 ${className}`}>
      {/* Main gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-700 to-gray-900" />

      {/* Gradient waves */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(59,91,219,0.15),transparent_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_0%_0%,rgba(59,91,219,0.1),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_100%_100%,rgba(59,91,219,0.1),transparent_50%)]" />
      </div>

      {/* Stable particles */}
      <div className={`absolute inset-0 ${variant === 'subtle' ? 'opacity-30' : 'opacity-100'}`}>
        {particles.map((particle, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: particle.left,
              top: particle.top,
            }}
          >
            {/* Halo */}
            {particle.hasHalo && (
              <div
                style={{
                  position: 'absolute',
                  width: `${particle.haloSize}px`,
                  height: `${particle.haloSize}px`,
                  transform: 'translate(-50%, -50%)',
                  background: `radial-gradient(circle, rgba(147, 197, 253, ${particle.haloOpacity}) 0%, transparent 70%)`,
                  borderRadius: '50%',
                }}
              />
            )}

            {/* Particle */}
            <div
              style={{
                position: 'absolute',
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                transform: 'translate(-50%, -50%)',
                backgroundColor: `rgba(147, 197, 253, ${particle.opacity})`,
                borderRadius: '50%',
              }}
            />
          </div>
        ))}
      </div>

      {/* Shooting stars */}
      {shootingStars.map(star => (
        <div
          key={star.id}
          id={star.id}
          className="absolute"
          style={{
            left: `${star.startX}%`,
            top: `${star.startY}%`,
            width: `${star.length}px`,
            height: `${star.size}px`,
            transform: `rotate(${star.angle}rad)`,
            transformOrigin: 'left center',
            animation: `shooting-star ${1 / star.speed}s linear forwards`,
            background: `linear-gradient(to right, 
              transparent 0%,
              rgba(147, 197, 253, ${star.opacity * 0.3}) 30%,
              rgba(147, 197, 253, ${star.opacity * 0.6}) 70%,
              rgba(255, 255, 255, ${star.opacity}) 100%
            )`,
            boxShadow: `0 0 ${star.size * 2}px rgba(147, 197, 253, ${star.opacity * 0.2})`,
            borderRadius: '50px',
          }}
          onAnimationEnd={e => {
            (e.target as HTMLElement).classList.add('completed');
          }}
        />
      ))}
    </div>
  );
}
